/**
 * The one place a lead becomes a contact.
 *
 * Approve requires a name and a company because `crm_contacts` requires them.
 * The server refuses a promotion without both (LEAD_MISSING_IDENTITY); this
 * dialog pre-fills them from the row and blocks submit while either is blank,
 * so that refusal is unreachable in normal use and honest if it ever fires.
 */

import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useReviewLead } from '@/hooks/crm/useLeadInbox';
import { leadDisplayName, type LeadInboxRow } from '@/lib/crm/lead-inbox';

export type ReviewDecision = 'approve' | 'reject' | 'snooze';

interface ReviewDialogProps {
  lead: LeadInboxRow;
  decision: ReviewDecision;
  onClose: () => void;
}

interface ReviewFormValues {
  name: string;
  company: string;
  note: string;
  snoozeUntil: string;
}

const TITLES: Record<ReviewDecision, string> = {
  approve: 'Approve and create a contact',
  reject: 'Reject this lead',
  snooze: 'Snooze this lead',
};

const DESCRIPTIONS: Record<ReviewDecision, string> = {
  approve:
    'This creates a CRM contact in My Turn, scheduled on the next business day that is not already full.',
  reject: 'The lead stays in the queue history and is never offered again.',
  snooze: 'The lead returns to the queue on the date you choose.',
};

function buildSchema(decision: ReviewDecision) {
  return z
    .object({
      name: z.string().trim().max(255),
      company: z.string().trim().max(255),
      note: z.string().max(2000),
      snoozeUntil: z.string(),
    })
    .superRefine((values, ctx) => {
      if (decision === 'approve') {
        if (values.name === '') {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['name'],
            message: 'A name is required to create a contact.',
          });
        }
        if (values.company === '') {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['company'],
            message: 'A company is required to create a contact.',
          });
        }
      }
      if (decision === 'snooze' && values.snoozeUntil === '') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['snoozeUntil'],
          message: 'Choose the date this lead should come back.',
        });
      }
    });
}

function defaultSnoozeDate(): string {
  const date = new Date();
  date.setDate(date.getDate() + 7);
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

export function ReviewDialog({ lead, decision, onClose }: ReviewDialogProps): JSX.Element {
  const reviewLead = useReviewLead();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ReviewFormValues>({
    resolver: zodResolver(buildSchema(decision)),
    defaultValues: {
      name: [lead.first_name, lead.last_name].filter(Boolean).join(' ').trim(),
      company: lead.company_name ?? '',
      note: '',
      snoozeUntil: defaultSnoozeDate(),
    },
  });

  const onSubmit = async (values: ReviewFormValues): Promise<void> => {
    try {
      const result = await reviewLead.mutateAsync({
        id: lead.id,
        decision,
        note: values.note.trim() === '' ? null : values.note.trim(),
        name: decision === 'approve' ? values.name : null,
        company: decision === 'approve' ? values.company : null,
        snoozeUntil:
          decision === 'snooze'
            ? new Date(`${values.snoozeUntil}T09:00:00`).toISOString()
            : null,
      });

      toast.success(
        decision === 'approve'
          ? `Contact created, due ${result.lead.dueDate ?? 'unscheduled'}`
          : decision === 'snooze'
            ? 'Lead snoozed'
            : 'Lead rejected',
      );
      onClose();
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : 'The decision could not be recorded. Try again.',
      );
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{TITLES[decision]}</DialogTitle>
          <DialogDescription>{DESCRIPTIONS[decision]}</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <p className="break-words font-medium" data-content-origin="external">
              {leadDisplayName(lead)}
            </p>
            <p className="break-words text-sm text-muted-foreground" data-content-origin="external">
              {[lead.job_title, lead.company_name].filter(Boolean).join(' · ') || '—'}
            </p>
            {lead.email && (
              <p className="break-all text-sm text-muted-foreground" data-content-origin="external">
                {lead.email}
              </p>
            )}
          </div>

          {decision === 'approve' && (
            <>
              <div className="space-y-1">
                <Label htmlFor="review-name">Name</Label>
                <Input id="review-name" {...register('name')} data-content-origin="external" />
                {errors.name && (
                  <p className="text-sm text-destructive" role="alert">{errors.name.message}</p>
                )}
              </div>
              <div className="space-y-1">
                <Label htmlFor="review-company">Company</Label>
                <Input id="review-company" {...register('company')} data-content-origin="external" />
                {errors.company && (
                  <p className="text-sm text-destructive" role="alert">{errors.company.message}</p>
                )}
              </div>
            </>
          )}

          {decision === 'snooze' && (
            <div className="space-y-1">
              <Label htmlFor="review-snooze">Come back on</Label>
              <Input id="review-snooze" type="date" {...register('snoozeUntil')} />
              {errors.snoozeUntil && (
                <p className="text-sm text-destructive" role="alert">{errors.snoozeUntil.message}</p>
              )}
            </div>
          )}

          <div className="space-y-1">
            <Label htmlFor="review-note">Note (optional)</Label>
            <Textarea
              id="review-note"
              rows={3}
              {...register('note')}
              placeholder="Why this decision, for whoever reads this queue next."
              data-content-origin="user"
            />
          </div>

          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              className="min-h-11 md:min-h-11"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button type="submit" className="min-h-11 md:min-h-11" disabled={reviewLead.isPending}>
              {reviewLead.isPending ? 'Saving…' : TITLES[decision]}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
